/**
 * 工具品牌图标解析（对齐客户端 packages/shared/src/utils/cli-icon-resolver.ts）。
 *
 * 客户端按 bash 命令解析出 CLI 工具名，再从 tool-icons.json 取 displayName + 图标，
 * 注入到 toolDisplayMeta.iconDataUrl。本项目 WS 不下发该字段，因此在前端做同样的解析：
 * 命令 → 命令名（跳过 env / sudo / 路径前缀 / shell -c 包装）→ tool-icons.json → 图标 URL。
 *
 * 图标文件放在 public/tool-icons/（与客户端 resources/tool-icons 一一对应）。
 */

import toolIconConfig from './tool-icons.json'

/** 透明前缀命令：真正干活的是后面那个命令，跳过它们。 */
const PREFIX_COMMANDS = new Set([
  'sudo',
  'time',
  'nice',
  'nohup',
  'env',
  'timeout',
  'strace',
  'ltrace',
  'ionice',
  'taskset',
  'watch',
  'caffeinate'
])

/** 命令名 → 工具条目的反查表（重复时先到先得，与客户端一致）。 */
const COMMAND_MAP = (() => {
  const map = Object.create(null)
  for (const tool of toolIconConfig.tools || []) {
    for (const cmd of tool.commands || []) {
      if (!map[cmd]) map[cmd] = tool
    }
  }
  return map
})()

/** 图标文件名 → 可直接用于 <img src> 的地址。 */
function iconUrlOf(filename) {
  return `${process.env.BASE_URL}tool-icons/${filename}`
}

/** 是否为环境变量赋值 token（如 FOO=bar）。 */
function isEnvAssignment(token) {
  return /^[A-Za-z_][A-Za-z0-9_]*=/.test(token)
}

/** 取路径最后一段：/usr/local/bin/node → node。 */
function baseName(token) {
  const normalized = String(token).replace(/\\/g, '/')
  const idx = normalized.lastIndexOf('/')
  return idx === -1 ? normalized : normalized.slice(idx + 1)
}

/** 轻量 shell 词法切分：去引号、按空白切（对齐 shell-quote 的常用行为）。 */
function tokenize(command) {
  const tokens = []
  let current = ''
  let inSingleQuote = false
  let inDoubleQuote = false
  for (let i = 0; i < command.length; i++) {
    const char = command[i]
    if (char === "'" && !inDoubleQuote) {
      inSingleQuote = !inSingleQuote
      continue
    }
    if (char === '"' && !inSingleQuote) {
      if (i > 0 && command[i - 1] === '\\') {
        current += char
        continue
      }
      inDoubleQuote = !inDoubleQuote
      continue
    }
    if (!inSingleQuote && !inDoubleQuote && /\s/.test(char)) {
      if (current) {
        tokens.push(current)
        current = ''
      }
      continue
    }
    current += char
  }
  if (current) tokens.push(current)
  return tokens
}

/**
 * 把一条命令按 && / || / | / ; 拆成多条子命令（引号内的不拆）。
 * @param {string} commandStr
 * @returns {string[]}
 */
export function splitCommands(commandStr) {
  const commands = []
  let current = ''
  let inSingleQuote = false
  let inDoubleQuote = false
  let i = 0

  while (i < commandStr.length) {
    const char = commandStr[i]
    const next = commandStr[i + 1]

    if (char === "'" && !inDoubleQuote) {
      inSingleQuote = !inSingleQuote
      current += char
      i++
      continue
    }
    if (char === '"' && !inSingleQuote) {
      if (i > 0 && commandStr[i - 1] === '\\') {
        current += char
        i++
        continue
      }
      inDoubleQuote = !inDoubleQuote
      current += char
      i++
      continue
    }

    if (!inSingleQuote && !inDoubleQuote) {
      if (char === '&' && next === '&') {
        if (current.trim()) commands.push(current.trim())
        current = ''
        i += 2
        continue
      }
      if (char === '|' && next === '|') {
        if (current.trim()) commands.push(current.trim())
        current = ''
        i += 2
        continue
      }
      if (char === '|') {
        if (current.trim()) commands.push(current.trim())
        current = ''
        i++
        continue
      }
      if (char === ';') {
        if (current.trim()) commands.push(current.trim())
        current = ''
        i++
        continue
      }
    }

    current += char
    i++
  }
  if (current.trim()) commands.push(current.trim())
  return commands
}

/**
 * 从单条子命令里取出真正的命令名。
 * @param {string} subCommand
 * @returns {string|undefined}
 */
export function extractCommandName(subCommand) {
  const tokens = tokenize(subCommand)
  let idx = 0

  while (idx < tokens.length && isEnvAssignment(tokens[idx])) idx++

  while (idx < tokens.length) {
    const cmdName = baseName(tokens[idx])

    if (PREFIX_COMMANDS.has(cmdName)) {
      idx++
      while (idx < tokens.length && tokens[idx].startsWith('-')) idx++
      if (cmdName === 'timeout' && idx < tokens.length && /^\d+/.test(tokens[idx])) idx++
      continue
    }

    // shell 包装：bash -lc 'git status' → 递归解析内层命令
    if (['bash', 'zsh', 'sh'].includes(cmdName)) {
      const remaining = tokens.slice(idx + 1)
      const cFlagIdx = remaining.findIndex(
        t => t === '-c' || (t.startsWith('-') && t.includes('c'))
      )
      if (cFlagIdx !== -1 && cFlagIdx + 1 < remaining.length) {
        return extractCommandName(remaining[cFlagIdx + 1])
      }
    }

    break
  }

  if (idx >= tokens.length) return undefined
  return baseName(tokens[idx])
}

/**
 * 取出命令串里所有命令名（按出现顺序）。
 * @param {string} commandStr
 * @returns {string[]}
 */
export function extractCommandNames(commandStr) {
  if (!commandStr || !String(commandStr).trim()) return []
  const names = []
  for (const sub of splitCommands(String(commandStr))) {
    const name = extractCommandName(sub)
    if (name) names.push(name)
  }
  return names
}

/**
 * 命令串 → 工具品牌（displayName + 图标 URL）。找不到时返回 null。
 * @param {string} commandStr
 * @returns {{id: string, displayName: string, icon: string}|null}
 */
export function resolveCommandBrand(commandStr) {
  for (const name of extractCommandNames(commandStr)) {
    const tool = COMMAND_MAP[name]
    if (!tool) continue
    return { id: tool.id, displayName: tool.displayName, icon: iconUrlOf(tool.icon) }
  }
  return null
}

/** 浏览器工具图标（客户端 BROWSER_TOOL_ICON_FILENAME = chrome.svg）。 */
export const BROWSER_TOOL_BRAND = {
  id: 'chrome',
  displayName: 'Chrome',
  icon: iconUrlOf('chrome.svg')
}
